import {useCallback, useEffect, useRef, useState} from "react";
import {useToRef} from "./useToRef";

const useKeepAwake = () => {
  const [isAwake, setIsAwake] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const wakeLockRef = useRef(null);

  const isAwakeRef = useToRef(isAwake)

  // Check if Wake Lock API is supported
  useEffect(() => {
    setIsSupported('wakeLock' in navigator);
  }, []);

  // Request wake lock
  const requestWakeLock = useCallback(async () => {
    if (!isSupported) {
      console.warn('Wake Lock API is not supported in this browser');
      return false;
    }

    if(isAwakeRef.current) return

    try {
      //@ts-ignore
      const wakeLock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = wakeLock;
      setIsAwake(true);

      // Handle wake lock release (e.g., when tab becomes inactive)
      wakeLock.addEventListener('release', () => {
        console.log('Wake lock was released');
        setIsAwake(false);
        wakeLockRef.current = null;
      });

      console.log('Screen wake lock activated');
      return true;
    } catch (err) {
      console.error('Failed to request wake lock:', err);
      return false;
    }
  }, [isSupported]);

  // Release wake lock
  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        //@ts-ignore
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setIsAwake(false);
        console.log('Screen wake lock released');
        return true;
      } catch (err) {
        console.error('Failed to release wake lock:', err);
        return false;
      }
    }
    return true;
  }, []);

  // Toggle wake lock
  const toggleWakeLock = useCallback(async () => {
    if (isAwake) {
      return await releaseWakeLock();
    } else {
      return await requestWakeLock();
    }
  }, [isAwake, releaseWakeLock, requestWakeLock]);

  // Auto-release wake lock when component unmounts
  useEffect(() => {
    return () => {
      if (wakeLockRef.current) {
        //@ts-ignore
        wakeLockRef.current.release();
      }
    };
  }, []);

  // Re-request wake lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isAwake && !wakeLockRef.current) {
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAwake, requestWakeLock]);

  return {
    isAwake,
    isSupported,
    requestWakeLock,
    releaseWakeLock,
    toggleWakeLock
  };
};

export default useKeepAwake
